<template>
  <div class="wrapper">
    <CCard class="mb-3">
      <CCardHeader class="d-flex align-items-center">
        <strong>iCare Detail</strong>
        <div v-if="canEditIcare && icare.id" class="ml-auto">
          <CButton size="sm" color="info" variant="outline" class="mr-2" @click="openEditIcare">Edit</CButton>
          <CButton size="sm" color="info" variant="outline" @click="openUploadPhoto">Upload Photo</CButton>
        </div>
      </CCardHeader>
      <CCardBody>
        <div class="icare-detail">
          <div class="icare-photo-wrap">
            <img v-if="icare.image && !icareImageError" :src="icare.image" class="icare-photo" alt="iCare photo" @error="icareImageError = true">
            <div v-else class="icare-photo icare-photo-empty">
              <font-awesome-icon icon="users" />
            </div>
          </div>
          <div class="icare-info">
            <div class="info-row"><strong>Name:</strong> {{ icare.name || '-' }}</div>
            <div class="info-row"><strong>ICL:</strong> {{ icare.icl_name || '-' }}</div>
            <div class="info-row"><strong>Email:</strong> {{ icare.icl_email || '-' }}</div>
            <div v-if="locationLabel" class="info-row"><strong>Location:</strong> {{ locationLabel }}</div>
            <div v-if="ageCategoryLabel" class="info-row"><strong>Age / Category:</strong> {{ ageCategoryLabel }}</div>
            <div v-if="scheduleLabel" class="info-row"><strong>Schedule:</strong> {{ scheduleLabel }}</div>
            <div v-if="icare.description" class="info-row"><strong>Description:</strong> {{ icare.description }}</div>
          </div>
        </div>
        <div class="summary-section mt-3">
          <div class="legend-title">Legend</div>
          <div class="legend-items mb-2">
            <span class="legend-item"><span class="dot dot-green"></span> Pending planted</span>
            <span class="legend-item"><span class="dot dot-yellow"></span> Pending remove planted</span>
          </div>
          <div class="summary-wrap">
            <span
              class="summary-dot dot-green"
              :class="{ active: summaryFilter === 'pending_planted' }"
              :title="summaryTooltip('Pending planted', icare.summary && icare.summary.pending_planted_count, 'Needs planted follow-up', icare.summary && icare.summary.last_recalculated_at)"
              @click="toggleSummaryFilter('pending_planted')"
            >{{ safeCount(icare.summary && icare.summary.pending_planted_count) }}</span>
            <span
              class="summary-dot dot-yellow"
              :class="{ active: summaryFilter === 'pending_remove_planted' }"
              :title="summaryTooltip('Pending remove planted', icare.summary && icare.summary.pending_remove_planted_count, 'Planted but inactive', icare.summary && icare.summary.last_recalculated_at)"
              @click="toggleSummaryFilter('pending_remove_planted')"
            >{{ safeCount(icare.summary && icare.summary.pending_remove_planted_count) }}</span>
          </div>
          <div v-if="summaryFilter !== 'all'" class="summary-filter-note mt-2">
            Filter active: {{ summaryFilterLabel(summaryFilter) }}
          </div>
        </div>
      </CCardBody>
    </CCard>

    <CCard>
      <CCardHeader>
        <strong>People</strong>
      </CCardHeader>
      <CCardBody>
        <div v-if="loading">Loading...</div>

        <div class="people-grid">
        <div v-for="person in filteredPeople" :key="person.people_uuid" class="person-item mb-3">
          <div class="person-head" @click="toggle(person.people_uuid)">
            <div class="person-head-left">
              <img :src="avatarSrc(person)" class="person-avatar" alt="" @error="onAvatarError(person)">
              <div class="person-name-row">
                <div class="person-name">{{ person.fullname }}</div>
                <CBadge color="primary" class="ml-2">{{ person.status }}</CBadge>
                <CBadge v-if="person.is_planted" color="success" class="ml-1">planted</CBadge>
              </div>
            </div>
            <div class="pending-chip-row">
              <span v-for="chip in personPendingChips(person)" :key="chip.key" class="pending-chip" :class="chip.className" :title="chip.title">
                {{ chip.label }}
              </span>
            </div>
            <div class="head-caret">▾</div>
          </div>

          <div v-if="expanded[person.people_uuid]" class="person-body">
            <div class="detail-group">
              <div class="detail-row"><strong>Name:</strong> {{ person.fullname }}</div>
              <div class="detail-row"><strong>Email:</strong> {{ person.email || '-' }}</div>
            </div>

            <div class="detail-group mt-3">
              <strong>Attendance List (This iCare):</strong>
              <span v-if="recapLoading[person.people_uuid]" class="ml-2">Loading...</span>
              <div class="attendance-list-wrap mt-2">
                <table class="attendance-table">
                  <thead>
                    <tr>
                      <th>iCare Date</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, idx) in getAttendanceHistory(person.people_uuid)" :key="'hist-' + idx">
                      <td>{{ item.date_label || '-' }}</td>
                      <td>
                        <span class="attendance-status" :class="item.attended ? 'ok' : 'miss'">
                          {{ item.attended ? 'Attended' : 'Missed' }}
                        </span>
                      </td>
                    </tr>
                    <tr v-if="getAttendanceHistory(person.people_uuid).length === 0">
                      <td colspan="2" class="attendance-empty">No iCare report found.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div v-if="visibleActions(person).length > 0" class="detail-group mt-4">
              <div class="d-flex flex-wrap">
                <CButton
                  v-for="action in visibleActions(person)"
                  :key="action.key"
                  v-b-tooltip.hover
                  :title="action.label"
                  :aria-label="action.label"
                  size="sm"
                  class="mr-2 mb-2 action-icon-btn"
                  :color="action.color"
                  @click="doAction(person, action.key)"
                >
                  <font-awesome-icon :icon="action.icon" />
                </CButton>
              </div>
            </div>
          </div>
        </div>
        </div>

        <div v-if="!loading && filteredPeople.length === 0">No people found.</div>
      </CCardBody>
    </CCard>

    <icaremodal></icaremodal>
    <modalphoto></modalphoto>
    <alertmodal></alertmodal>
  </div>
</template>

<script>
import axios from 'axios'
import EventBus from '@/eventBus'
import icarePeopleDetail from '@/services/icarePeopleDetail'
import icaremodal from './icare_modal'
import modalphoto from './icare_modal_photo.vue'
import alertmodal from '@/views/alert/modal.vue'

const EDIT_ICARE_PERMISSION = 'Create/edit iCare (including edit leader & core team)'

export default {
  name: 'IcarePeopleDetailGroup',
  components: { icaremodal, modalphoto, alertmodal },
  data() {
    return {
      icareImageError: false,
      avatarErrors: {},
      loading: false,
      icare: {},
      people: [],
      summaryFilter: 'all',
      expanded: {},
      attendance: {},
      recapLoading: {}
    }
  },
  computed: {
    canEditIcare() {
      // Permissions load async after login; treat "not loaded yet" as no access.
      const authenticated = this.$store.state.user && this.$store.state.user.authenticated
      const permission = authenticated && authenticated.permission
      return Array.isArray(permission) && permission.indexOf(EDIT_ICARE_PERMISSION) !== -1
    },
    locationLabel() {
      return this.joinParts([this.icare.location, this.icare.zone])
    },
    ageCategoryLabel() {
      return this.joinParts([this.formatAgeRange(this.icare.age_min, this.icare.age_max), this.icare.category])
    },
    scheduleLabel() {
      const day = this.icare.meeting_day
      const time = this.icare.meeting_time ? String(this.icare.meeting_time).slice(0, 5) : ''
      return this.joinParts([day, time], ' ')
    },
    filteredPeople() {
      const list = Array.isArray(this.people) ? this.people : []
      if (this.summaryFilter === 'all') return list

      return list.filter((person) => {
        const flags = person && person.pending_flags ? person.pending_flags : {}
        if (this.summaryFilter === 'pending_planted') return !!flags.pending_planted
        if (this.summaryFilter === 'pending_remove_planted') return !!flags.pending_remove_planted
        return true
      })
    }
  },
  mounted() {
    EventBus.$on('TABLE_UPDATE', this.onIcareUpdated)
    this.loadDetail()
  },
  beforeDestroy() {
    EventBus.$off('TABLE_UPDATE', this.onIcareUpdated)
  },
  methods: {
    onIcareUpdated() {
      this.loadDetail()
    },
    openEditIcare() {
      if (!this.canEditIcare) return
      EventBus.$emit('OPEN_MODAL_ICARE', { title: 'Edit iCare', mode: 'edit', data: this.icare.id })
    },
    openUploadPhoto() {
      if (!this.canEditIcare) return
      EventBus.$emit('OPEN_MODAL_ICARE_PHOTO', { title: 'Icare Image', mode: 'edit', data: this.icare.id })
    },
    joinParts(parts, separator = ' • ') {
      return parts
        .map((part) => (part === null || part === undefined ? '' : String(part).trim()))
        .filter(Boolean)
        .join(separator)
    },
    formatAgeRange(min, max) {
      const hasMin = min !== null && min !== undefined
      const hasMax = max !== null && max !== undefined
      if (!hasMin && !hasMax) return ''
      if (!hasMax) return `${min}+ yo.`
      return `${hasMin ? min : 0}-${max} yo.`
    },
    avatarSrc(person) {
      if (person.photo_url && !this.avatarErrors[person.people_uuid]) return person.photo_url
      return axios.defaults.baseURL + '/getfile/memberphoto/no_photo.jpg'
    },
    onAvatarError(person) {
      if (person.photo_url && !this.avatarErrors[person.people_uuid]) {
        this.$set(this.avatarErrors, person.people_uuid, true)
      }
    },
    toggleSummaryFilter(filterKey) {
      this.summaryFilter = this.summaryFilter === filterKey ? 'all' : filterKey
    },
    summaryFilterLabel(filterKey) {
      if (filterKey === 'pending_planted') return 'Pending planted'
      if (filterKey === 'pending_remove_planted') return 'Pending remove planted'
      return 'All'
    },
    loadDetail() {
      this.loading = true
      icarePeopleDetail
        .getIcareDetail(this.$route.params.id)
        .then((res) => {
          this.icare = (res.data && res.data.icare) || {}
          this.people = (res.data && res.data.people) || []
          this.icareImageError = false
          this.avatarErrors = {}

          // Default accordion state: open all people rows on initial load.
          this.expanded = {}
          this.people.forEach((person) => {
            const peopleUuid = person && person.people_uuid
            if (!peopleUuid) return

            this.$set(this.expanded, peopleUuid, true)
            if (!this.attendance[peopleUuid]) {
              const relationshipType = person && person.status ? String(person.status).toLowerCase() : ''
              this.loadRecap(peopleUuid, relationshipType)
            }
          })
        })
        .catch((err) => {
          alert((err.response && err.response.data && err.response.data.message) || 'Failed loading iCare detail')
        })
        .finally(() => {
          this.loading = false
        })
    },
    toggle(peopleUuid) {
      this.$set(this.expanded, peopleUuid, !this.expanded[peopleUuid])
      if (this.expanded[peopleUuid] && !this.attendance[peopleUuid]) {
        const person = this.people.find((item) => item.people_uuid === peopleUuid)
        const relationshipType = person && person.status ? String(person.status).toLowerCase() : ''
        this.loadRecap(peopleUuid, relationshipType)
      }
    },
    loadRecap(peopleUuid, relationshipType) {
      this.$set(this.recapLoading, peopleUuid, true)
      icarePeopleDetail
        .getAttendanceRecap(this.$route.params.id, peopleUuid, { relationship_type: relationshipType })
        .then((res) => {
          this.$set(this.attendance, peopleUuid, (res.data && res.data.attendance) || [])
        })
        .catch(() => {
          this.$set(this.attendance, peopleUuid, [])
        })
        .finally(() => {
          this.$set(this.recapLoading, peopleUuid, false)
        })
    },
    doAction(person, action) {
      if (!confirm('Are you sure you want to ' + action.replaceAll('_', ' ') + ' for ' + person.fullname + '?')) {
        return
      }

      const payload = { action }
      const reasonRequiredActions = ['set_planted', 'unset_planted', 'remove_visitor', 'remove_member', 'move_member', 'move_visitor']

      if (reasonRequiredActions.includes(action)) {
        const reason = prompt('Please provide reason:')
        if (reason === null) return
        if (!reason.trim()) {
          alert('Reason is required')
          return
        }
        payload.reason = reason.trim()
      }

      if (action === 'move_member' || action === 'move_visitor') {
        const target = prompt('Target iCare ID:')
        if (target === null) return
        const targetIcareId = Number(target)
        if (!Number.isInteger(targetIcareId) || targetIcareId <= 0) {
          alert('Target iCare ID must be a valid number')
          return
        }
        payload.target_icare_id = targetIcareId
      }

      icarePeopleDetail
        .submitAction(this.$route.params.id, person.people_uuid, payload)
        .then(() => {
          alert('Action success')
          this.loadDetail()
        })
        .catch((err) => {
          alert((err.response && err.response.data && err.response.data.message) || 'Action failed')
        })
    },
    getAttendanceHistory(peopleUuid) {
      const raw = this.attendance[peopleUuid] || []
      return Array.isArray(raw) ? raw : []
    },
    visibleActions(person) {
      const status = (person.status || '').toLowerCase()
      if (status === 'icl') {
        return []
      }

      const actions = []

      if (person.is_planted) {
        actions.push({ key: 'unset_planted', label: 'Unset Planted', color: 'warning', icon: 'seedling' })
      } else {
        actions.push({ key: 'set_planted', label: 'Set Planted', color: 'success', icon: 'seedling' })
      }

      if (status === 'visitor') {
        actions.push({ key: 'remove_visitor', label: 'Remove Visitor', color: 'danger', icon: 'trash-alt' })
        actions.push({ key: 'move_visitor', label: 'Move Visitor', color: 'dark', icon: 'exchange-alt' })
      }

      if (status === 'member') {
        actions.push({ key: 'remove_member', label: 'Remove Member', color: 'danger', icon: 'trash-alt' })
        actions.push({ key: 'promote_core_team', label: 'Promote Core Team', color: 'info', icon: 'user-plus' })
        actions.push({ key: 'move_member', label: 'Move Member', color: 'dark', icon: 'exchange-alt' })
      }

      if (status === 'core team') {
        actions.push({ key: 'remove_member', label: 'Remove Member', color: 'danger', icon: 'trash-alt' })
        actions.push({ key: 'demote_core_team', label: 'Demote Core Team', color: 'secondary', icon: 'user-minus' })
      }

      return actions
    },
    personPendingChips(person) {
      const flags = person && person.pending_flags ? person.pending_flags : {}
      const chips = []

      if (flags.pending_planted) {
        chips.push({
          key: 'pp',
          label: 'Pending Planted',
          className: 'chip-green',
          title: 'Needs planted follow-up'
        })
      }

      if (flags.pending_remove_planted) {
        chips.push({
          key: 'prp',
          label: 'Pending Remove Planted',
          className: 'chip-yellow',
          title: 'Planted but inactive'
        })
      }

      return chips
    },
    safeCount(value) {
      const parsed = Number(value)
      if (!Number.isFinite(parsed)) return 0
      return parsed
    },
    summaryTooltip(label, count, definition, recalculatedAt) {
      return `${label}: ${this.safeCount(count)}\nDefinition: ${definition}\nLast recalculated: ${recalculatedAt || '-'}`
    }
  }
}
</script>

<style scoped>
.legend-title {
  font-weight: 700;
  margin-bottom: 4px;
}
.legend-items {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 12px;
}
.legend-item {
  display: inline-flex;
  align-items: center;
}
.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 6px;
  border: 1px solid #9da5b1;
}
.dot-green { background: #2eb85c; }
.dot-yellow { background: #f9b115; }
.summary-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
}
.summary-dot {
  min-width: 34px;
  height: 34px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 700;
  font-size: 13px;
  padding: 0 8px;
  cursor: pointer;
  border: 2px solid transparent;
}
.summary-dot.active {
  border-color: #1f2937;
  box-shadow: 0 0 0 2px rgba(31, 41, 55, 0.15);
}
.summary-filter-note {
  font-size: 12px;
  color: #334155;
}
.info-row {
  margin-bottom: 8px;
}

.person-item {
  border: 1px solid #d8dee8;
  border-radius: 10px;
  overflow: hidden;
}
.people-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.icare-detail {
  display: flex;
  align-items: flex-start;
  gap: 20px;
}
.icare-photo-wrap {
  flex: 0 0 auto;
}
.icare-photo {
  width: 140px;
  height: 140px;
  border-radius: 10px;
  object-fit: cover;
  display: block;
  background: #eef2f7;
}
.icare-photo-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9da5b1;
  font-size: 40px;
  border: 1px solid #d8dee8;
}
.icare-info {
  flex: 1 1 auto;
  min-width: 0;
}
.person-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 10px;
  flex: 0 0 auto;
  background: #eef2f7;
}
.action-icon-btn {
  min-width: 36px;
}

.person-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  cursor: pointer;
  background: #dce4ef;
  border-bottom: 1px solid #cdd7e4;
}
.person-head-left {
  min-width: 0;
  display: flex;
  align-items: center;
}
.person-name-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}
.head-caret {
  color: #5f6b7a;
  font-size: 14px;
}

.person-name {
  font-weight: 700;
}
.pending-chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-left: auto;
  justify-content: flex-end;
  max-width: 45%;
}
.pending-chip {
  font-size: 10px;
  font-weight: 700;
  border-radius: 999px;
  padding: 2px 6px;
  display: inline-block;
  color: #fff;
}
.chip-green { background: #2eb85c; }
.chip-yellow { background: #f9b115; color: #1f2d3d; }

.person-body {
  padding: 16px;
  background: #ffffff;
}

.detail-group {
  margin-bottom: 8px;
}

.detail-row {
  margin-bottom: 8px;
}

.attendance-list-wrap {
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0;
  background: #f8fafc;
}
.attendance-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}
.attendance-table th,
.attendance-table td {
  padding: 8px 10px;
  border-bottom: 1px solid #e5e7eb;
  text-align: left;
}
.attendance-table th {
  position: sticky;
  top: 0;
  background: #eef2f7;
  z-index: 1;
}
.attendance-status.ok {
  color: #2e7d32;
  font-weight: 700;
}
.attendance-status.miss {
  color: #c62828;
  font-weight: 700;
}
.attendance-empty {
  color: #64748b;
  font-size: 12px;
}
@media (max-width: 1199.98px) {
  .people-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 767.98px) {
  .people-grid {
    grid-template-columns: 1fr;
  }
  .icare-detail {
    flex-direction: column;
  }
}
@media (max-width: 991.98px) {
  .pending-chip-row {
    max-width: 100%;
  }
}
</style>
